import { ContentContainer } from "components/ui/display/ContentContainer";
import { useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { Button } from "components/ui/controls/Button";
import { DropdownMenu, DropdownOption } from "components/ui/inputs/DropdownMenu";
import { ProjectsContext } from "providers/ProjectsProvider";
import { TasksContext } from "providers/TasksProvider";
import { HeaderInput } from "components/ui/inputs/HeaderInput";
import { useApi } from "providers/UserProvider";
import { DragDropProvider } from "@dnd-kit/react";

import { move } from "@dnd-kit/helpers";
import { useErrors } from "providers/ErrorsProvider";
import { TaskUndertask } from "components/features/tasks/TaskUndertask";
import { EditorInput } from "components/ui/inputs/EditorInput";
import { Input } from "components/ui/inputs/Input";
import { formatLocalDate } from "utilities/dateAndTime";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const TaskSettings = () => {

    const { api } = useApi();

    const navigate = useNavigate()
    const { projectUuid, taskUuid } = useParams();
    const [ editedTask, setEditedTask ] = useState(null);
    const [ newTaskDescription, setNewTaskDescription ] = useState('');

    const { projects } = useContext(ProjectsContext);
    const { tasks, tasksLoading, handleTasksEdit } = useContext(TasksContext);

    const { setError, unsetError } = useErrors();


    const [ availableDeliveries, setAvailableDeliveries ] = useState(() => {
        if(!projectUuid) return null;
        else
        {
            const selectedProject = projects.find(prj => prj.projectUuid === projectUuid);
            if(!selectedProject) return null;

            if(selectedProject.projectDeliveries && selectedProject.projectDeliveries.enabled)
            {
                return selectedProject.projectDeliveries.deliveries;
            }
        }
    });

    const closeFunction = useCallback(() => {

        unsetError("TASK_TITLE_TOO_LONG");
        unsetError("TASK_TITLE_TOO_SHORT");
        unsetError("TASK_PROJECT_NOT_SET");
        unsetError("TASK_PRIORITY_NOT_SET");
        unsetError("TASK_DURATION_IS_NULL");
        unsetError("TASK_DEADLINE_BEFORE_STARTDATE");

        if (projectUuid) navigate('/projects/' + projectUuid);
        else navigate('/tasks');
    }, [navigate, projectUuid, unsetError]);

    const saveFunction = (e) => {
        let changesValidation = {
            taskTitle: true,
            taskProject: true,
            taskPriority: true,
            taskDuration: true,
            taskDeadline: true,
            taskStart: true,
        }



        if(editedTask.taskTitle.length < 5)
        {
            setError("TASK_TITLE_TOO_SHORT");
            unsetError("TASK_TITLE_TOO_LONG");
            changesValidation.taskTitle = false;
        }
        else if(editedTask.taskTitle.length > 100)
        {
            setError("TASK_TITLE_TOO_LONG");
            unsetError("TASK_TITLE_TOO_SHORT");
            changesValidation.taskTitle = false;
        }
        else
        {
            unsetError("TASK_TITLE_TOO_LONG");
            unsetError("TASK_TITLE_TOO_SHORT");
        }

        if(!editedTask.taskProjectUuid)
        {
            setError("TASK_PROJECT_NOT_SET");
            changesValidation.taskProject = false;
        }
        else unsetError("TASK_PROJECT_NOT_SET");

        if(!editedTask.taskPriority)
        {
            setError("TASK_PRIORITY_NOT_SET");
            changesValidation.taskPriority = false;
        }
        else unsetError("TASK_PRIORITY_NOT_SET");

        if(!editedTask.taskDuration)
        {
            setError("TASK_DURATION_IS_NULL");
            changesValidation.taskDuration = false;
        }
        else unsetError("TASK_DURATION_IS_NULL");

        console.log(editedTask.taskStart, editedTask.taskDeadline)
        if(editedTask.taskDeadline < editedTask.taskStart)
        {
            console.log(editedTask.taskStart, editedTask.taskDeadline)
            setError("TASK_DEADLINE_BEFORE_STARTDATE");
            changesValidation.taskDeadline = false;
            changesValidation.taskStart = false;
        }
        else unsetError("TASK_DEADLINE_BEFORE_STARTDATE");
    
        if(!Object.values(changesValidation).every(Boolean)) return;

        if(editedTask.taskUuid)
        {
            handleTasksEdit({
                taskUuid: editedTask.taskUuid,
                action: "UPDATE TASK",
                changes: editedTask
            }).then(res => {
                if(res?.status) setError(res.reason);
            });
        }
        else
        {
            handleTasksEdit({
                taskUuid: crypto.randomUUID(),
                action: "CREATE TASK",
                changes: editedTask
            }).then(res => {
                if(res?.status) setError(res.reason);
            });
        }

        closeFunction();
    }

    const projectChangeHandler = useCallback((selectedProjectUuid) => {

        const selectedProject = projects.find(prj => prj.projectUuid === selectedProjectUuid);
        
        if(!selectedProject)
        {
            setAvailableDeliveries(null);
            return;
        }

        setEditedTask(prev => ({...prev, taskProjectUuid: selectedProjectUuid}));

        if(selectedProject.projectDeliveries && selectedProject.projectDeliveries.enabled)
        {
            setAvailableDeliveries(selectedProject.projectDeliveries.deliveries);
        }
        else
        {
            setAvailableDeliveries(null);
        }

        return;
    }, [projects]);

    useEffect(() => {
        if(tasksLoading) return;
        
        if(taskUuid === 'new')
        {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            const nextWeek = new Date(today);
            nextWeek.setDate(today.getDate() + 7);

            

            setEditedTask({
                taskUuid: null,
                taskTitle: '',
                taskDescription: '',
                taskStatus: 'NotStarted',
                taskStart: new Date(formatLocalDate(today)),
                taskDeadline: nextWeek,
                taskPriority: null,
                taskFinished: null,
                taskDuration: null,
                taskProjectUuid: projectUuid ? projectUuid : null,
                taskUndertasks: []  
            });

            projectChangeHandler(null)
        }
        else if(taskUuid)
        {
            let task = tasks.find(task => task.taskUuid === taskUuid);
            if(task)
            {
                setEditedTask(task);

                api.request('tasks/query/description', {taskUuid: taskUuid}).then(res => {
                    setEditedTask(prev => ({...prev, taskDescription: res.taskDescription}));
                });

                
                projectChangeHandler(task.taskProjectUuid);
            }
            else
            {
                setEditedTask(null)
                return;
            }
            
        }
        else
        {
            setEditedTask(null);
        }
        
    }, [api, taskUuid, tasks, tasksLoading, projectUuid, projectChangeHandler]);

    let undertaskskUuidOrder = editedTask && editedTask.taskUndertasks ? editedTask.taskUndertasks.map(ut => ut ? ut.undertaskUuid : null) : [];

    const onSortEnd = (e) => {
        const newOrder = move(undertaskskUuidOrder, e);

        const newOrderArray = newOrder.map(i => {
            const undertask = editedTask.taskUndertasks.find(ut => ut.undertaskUuid === i)
            return undertask;
        })

        setEditedTask(prev => ({...prev, taskUndertasks: newOrderArray}));
    }

    if(!editedTask || editedTask.taskUuid === undefined)
    {
        return null;
    } 

    return(
        createPortal(
            <StyledTaskSettings>
                <ContentContainer
                    padding='none'
                >
                    <div className="taskSettingsHeader">
                        <div className="taskSettingsHeaderTitle">
                            <div>
                                <MaterialIcon name='ballot' weight={300}/>
                            </div>
                            Redigering av oppgave
                        </div>
                        <MaterialIcon name='close' weight={300} style={{cursor: 'pointer'}} onClick={closeFunction}/>
                    </div>
                    <div className="taskSettingsContent">
                        <div className="taskSettingsInfo">
                            
                            <DropdownMenu
                                defaultName='Prosjekt'
                                defaultIcon={<MaterialIcon name='folder_open' weight={300}/>}
                                defaultValue={null}
                                label="Velg prosjekt..."
                                defaultSelected={editedTask.taskProjectUuid}
                                onSelect={projectChangeHandler}
                            >
                                {
                                    projects.map(prj => {
                                        if(!prj.projectTasks.enabled) return null;
                                        return (
                                            <DropdownOption
                                                key={prj.projectUuid}
                                                name={prj.projectName}
                                                value={prj.projectUuid}
                                            />
                                        )
                                    }
                                        
                                    )
                                }
                            </DropdownMenu>
                            <DropdownMenu
                                defaultName={"Velg leveranse..."}
                                defaultIcon={<MaterialIcon
                                    name='package_2'
                                    weight={300}
                                    color={({theme}) => theme.colors.mutedForeground}
                                />}
                                defaultValue={null}
                                label={"Leveranse"}
                                defaultSelected={editedTask.taskDelivery}
                                onSelect={(e) => setEditedTask(prev => ({...prev, taskDelivery: e}))}
                                disabled={!availableDeliveries}
                            >
                                {
                                    availableDeliveries ? 
                                    availableDeliveries.map(del =>
                                        <DropdownOption
                                            key={del.deliveryUuid}
                                            name={del.title}
                                            value={del.deliveryUuid}
                                        />
                                    )
                                    : null
                                }
                                <DropdownOption
                                    name={"Tøm leveranse"}
                                    value={null}
                                    icon={<MaterialIcon name='close' weight={300}/>}
                                />
                            </DropdownMenu>
                            
                            
                            <DropdownMenu
                                defaultName="Prioritet"
                                defaultIcon={<MaterialIcon name='flag' weight={300}/>}
                                defaultValue={null}
                                defaultSelected={editedTask.taskPriority ? editedTask.taskPriority : null}
                                onSelect={(e) => {setEditedTask(prev => ({...prev, taskPriority: e}))}}
                                label="Prioritet"
                            >
                                <DropdownOption
                                    name='Lav'
                                    value={1}
                                    icon={<MaterialIcon name='flag' weight={300} color={({theme}) => theme.colors.successForeground}/>}
                                />
                                <DropdownOption
                                    name='Moderat'
                                    value={2}
                                    icon={<MaterialIcon name='flag' weight={300} color={({theme}) => theme.colors.warningForeground}/>}
                                />
                                <DropdownOption
                                    name='Høy'
                                    value={3}
                                    icon={<MaterialIcon name='flag' weight={300} color={({theme}) => theme.colors.errorForeground}/>}
                                />
                            </DropdownMenu>

                            <Input
                                prefix={<MaterialIcon name='schedule' weight={300}/>}
                                sufix={'t.'}
                                title="Arbeidsmengde"
                                type="number"
                                value={editedTask.taskDuration}
                                onChange={(e) => setEditedTask(prev => ({...prev, taskDuration: Math.max(Math.abs(Number(e.target.value)), 1)}))}
                                
                            />

                            <Input
                                prefix={<MaterialIcon name='calendar_today' weight={300}/>}
                                title="Startdato"
                                type="date"
                                value={editedTask.taskStart}
                                onChange={(e) => setEditedTask(prev => ({...prev, taskStart: new Date(e)}))}
                            />

                            <Input
                                prefix={<MaterialIcon name='calendar_today' weight={300}/>}
                                title="Frist"
                                type="date"
                                value={editedTask.taskDeadline}
                                onChange={(e) => setEditedTask(prev => ({...prev, taskDeadline: new Date(e)}))}
                            />
                            
                        </div>
                        <div className="taskSettingsDescription">
                            <HeaderInput
                                value={editedTask.taskTitle}
                                onChange={(e) => setEditedTask(prev => ({...prev, taskTitle: e}))}
                                textsize='xl'
                                placeholder="Oppgavetittel"
                            />
                            <EditorInput
                                size='sm'
                                onChange={(e) => {
                                    if(!editedTask) return;
                                    setEditedTask(prev => ({...prev, taskDescription: e}))
                                }}
                                value={editedTask.taskDescription}
                                title="Oppgavebeskrivelse"
                            />
                            <div className="undertasksContainer">
                                <span>
                                    Underoppgaver
                                </span>

                                <div className="undertasksContent">
                                    <DragDropProvider onDragEnd={onSortEnd}>
                                    {
                                        editedTask.taskUndertasks ? editedTask.taskUndertasks.map((ut, i) =>
                                            <TaskUndertask
                                                key={ut.undertaskUuid}
                                                undertask={ut}
                                                index={i}
                                                onChange={(e) => {
                                                    setEditedTask(prev => ({
                                                        ...prev,
                                                        taskUndertasks: prev.taskUndertasks.map(undertask => {
                                                            if(undertask.undertaskUuid !== ut.undertaskUuid) return undertask;

                                                            return {
                                                                undertaskUuid: undertask.undertaskUuid,
                                                                undertaskDescription: undertask.undertaskDescription,
                                                                finished: e
                                                            }
                                                        })
                                                    }))
                                                }}

                                                onRemove={() => {
                                                    setEditedTask(prev => {
                                                        let undertasks = prev.taskUndertasks.filter(undertask => undertask.undertaskUuid !== ut.undertaskUuid);

                                                        return {...prev, taskUndertasks: undertasks};
                                                    })
                                                }}
                                            />)
                                            : null
                                    }
                                    </DragDropProvider>
                                </div>    
                                <div className="newUndertaskContainer">
                                    <div className="iconContainer">
                                        <MaterialIcon name='add' weight={300} size='lg'/>
                                    </div>
                                    <input
                                        value={newTaskDescription}
                                        onChange={(e) => setNewTaskDescription(e.target.value)}
                                        placeholder="Ny underoppgave"
                                    />
                                    <Button
                                        type='secondary'
                                        text="Legg til"
                                        onClick={() => {
                                            const newUndertask = {
                                                undertaskUuid: crypto.randomUUID(),
                                                undertaskDescription: newTaskDescription,
                                                finished: false
                                            }
                                            
                                            setEditedTask(prev => ({...prev, taskUndertasks: [...prev.taskUndertasks, newUndertask]}));
                                            setNewTaskDescription('');
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="taskSettingsFooter">
                        <Button type='secondary' text='Forkast' onClick={closeFunction}/>
                        <Button prefix={<MaterialIcon name='save' weight={300}/>} text='Lagre' onClick={saveFunction}/>
                    </div>
                </ContentContainer>
            </StyledTaskSettings>
            
            ,document.getElementById("modalRoot")
        )
    
    )
}

const StyledTaskSettings = styled.div`
    height: 100%;
    width: 100%;
    background: rgba(${({theme}) => theme.colors.neutralSecondaryForegroundRGB}, 0.75);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;

    > div
    {
        border-radius: ${({theme}) => theme.cornerRadius.lg};
    }

    div.taskSettingsHeader
    {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: ${({theme}) => theme.spacing.lg} ${({theme}) => theme.spacing.lg};

        div.taskSettingsHeaderTitle
        {
            display: flex;
            align-items: center;
            font-weight: 600;
            gap: ${({theme}) => theme.spacing.md};

            > div
            {
                padding: ${({theme}) => theme.spacing.xs};
                background: ${({theme}) => theme.colors.neutralSecondaryForeground};
                border-radius: ${({theme}) => theme.cornerRadius.sm};
                display: flex;
                align-items: center;
                justify-content: center;

                .icon
                {
                    font-size: ${({theme}) => theme.iconSize.md};
                    color: ${({theme}) => theme.colors.neutralPrimaryBackground};
                }
            }
        }

    }

    div.taskSettingsContent
    {
        display: flex;
        height: 650px;
        overflow: hidden;
        border-top: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};

        div.taskSettingsInfo
        {
            background: ${({theme}) => theme.colors.neutralSecondaryBackground};
            width: 300px;
            padding: ${({theme}) => theme.spacing.lg} ${({theme}) => theme.spacing.lg};
            border-right: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
            display: flex;
            flex-direction: column;
            gap: ${({theme}) => theme.spacing.lg};
            overflow: auto;
        }

        div.taskSettingsDescription
        {
            padding: ${({theme}) => theme.spacing.lg} ${({theme}) => theme.spacing.lg};
            width: 600px;
            display: flex;
            flex-direction: column;
            gap: ${({theme}) => theme.spacing.lg};
            

            div.undertasksContainer
            {
                overflow: hidden;
                display: flex;
                flex-direction: column;
                gap: ${({theme}) => theme.spacing.md};

                span
                {
                    font-size: ${({theme}) => theme.fontSize.md};
                    font-weight: 500;
                }

                div.undertasksContent
                {
                    display: flex;
                    flex-direction: column;
                    gap: ${({theme}) => theme.spacing.md};
                    overflow: auto;
                    flex: 1;
                }

                div.newUndertaskContainer
                {
                    
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: ${({theme}) => theme.spacing.md};
                    padding-left: calc(15px + ${({theme}) => theme.spacing.md});

                    div.iconContainer
                    {
                        border: 1px solid ${({theme}) => theme.colors.mutedForeground};
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: ${({theme}) => theme.cornerRadius.sm};
                        height: 18px;
                        width: 18px;

                        svg
                        {
                            color: ${({theme}) => theme.colors.mutedForeground};
                            height: 18px;
                            width: 18px;
                        }
                    }
                    
                    input
                    {
                        flex: 1;
                        border: none;
                        padding: ${({theme}) => theme.spacing.sm} ${({theme}) => theme.spacing.sm};
                        margin: 0;
                        outline: none;
                        font-size: ${({theme}) => theme.fontSize.md};
                    }
                }
            }
        }
    }

    div.taskSettingsFooter
    {
        padding: ${({theme}) => theme.spacing.lg} ${({theme}) => theme.spacing.lg};
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: ${({theme}) => theme.spacing.md};
    }
`